// src/components/Login/AuthContext.js
// UNIFIED AUTH CONTEXT - Single source of truth for authentication
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../utils/axiosConfig';
import guestApi from '../../lib/guestApi';
import { getOrCreateDeviceId } from '../../utils/deviceId';
import { TokenService } from '../../utils/TokenService';
import { decodeJWT, isTokenExpired, getRefreshDelay } from '../../utils/jwtHelper';

// Create Auth Context
const AuthContext = createContext();

// API URL
const API_URL = process.env.REACT_APP_API_URL;

// Singleton refresh promise to prevent multiple simultaneous refreshes
let refreshPromise = null;

const getSafeReturnUrl = (rawReturnUrl) => {
  const allowedReturnUrls = ['/tour-guide', '/review_input', '/coupon_input'];
  const isTourShareLink = /^\/join-tour\/[A-Za-z0-9_-]+$/.test(rawReturnUrl || '');
  return allowedReturnUrls.includes(rawReturnUrl) || isTourShareLink
    ? rawReturnUrl
    : '/dashboard';
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs for refresh timer management
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * CORE: Set authentication state
   * Saves token, sets user, updates axios headers, schedules refresh
   */
  const setAuth = useCallback(({ accessToken, user }) => {
    console.log('📝 [AUTH] setAuth called:', { hasToken: !!accessToken, user: user?.name, role: user?.role });

    if (!accessToken || !user) {
      console.warn('[AUTH] Invalid setAuth call - missing token or user');
      return;
    }

    // Clear explicit logout flag (user is now logged in)
    localStorage.removeItem('explicitLogout');

    // Save to TokenService (handles memory + legacy localStorage)
    TokenService.setAccessToken(accessToken);
    localStorage.setItem('user', JSON.stringify(user));

    // Set axios default header
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    // Update state
    setCurrentUser(user);

    // Schedule proactive refresh (not for guests)
    scheduleTokenRefresh(accessToken, user);

    console.log('✅ [AUTH] Auth state set successfully');
  }, []);

  /**
   * CORE: Clear authentication state
   * Removes tokens, clears user, cancels timers
   */
  const clearAuth = useCallback(() => {
    console.log('🧹 [AUTH] Clearing auth state');

    // Clear tokens via TokenService
    TokenService.clearAccessToken();
    refreshPromise = null;

    // Set logout flag to prevent auto-restore from cookie
    localStorage.setItem('explicitLogout', 'true');
    console.log('🚪 [AUTH] Set explicit logout flag');

    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('interestedEvents');
    localStorage.removeItem('joiningEvents');

    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    delete api.defaults.headers.common['Authorization'];

    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Update state
    setCurrentUser(null);

    console.log('✅ [AUTH] Auth state cleared');
  }, []);

  /**
   * CORE: Refresh access token using httpOnly refresh cookie
   */
  const refreshAccessToken = useCallback(async () => {
    console.log('🔄 [AUTH] Attempting token refresh...');

    try {
      const response = await api.get('/refresh', {
        withCredentials: true // Send httpOnly cookie
      });

      if (response.data?.accessToken) {
        const { accessToken, user: refreshedUser } = response.data;

        // Use existing user if not provided
        const user = refreshedUser || currentUser || JSON.parse(localStorage.getItem('user') || 'null');

        if (user) {
          setAuth({ accessToken, user });
          console.log('✅ [AUTH] Token refreshed successfully');
          return accessToken;
        }
      }

      throw new Error('Invalid refresh response');
    } catch (error) {
      console.error('❌ [AUTH] Token refresh failed:', error.response?.status || error.message);

      // Don't clear auth immediately - let the 401 interceptor handle it
      // This prevents logout loops
      throw error;
    }
  }, [currentUser, setAuth]);

  /**
   * Get refreshed token (singleton - queues multiple calls)
   */
  const getRefreshedTokenOnce = useCallback(async () => {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken()
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }, [refreshAccessToken]);

  /**
   * Schedule proactive token refresh
   */
  const scheduleTokenRefresh = useCallback((accessToken, user) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Don't refresh for guests
    if (user?.role === 'guest') {
      console.log('⏭️ [AUTH] Skipping refresh schedule for guest');
      return;
    }

    const delay = getRefreshDelay(accessToken);

    if (delay <= 0) {
      console.log('⚠️ [AUTH] Token already expired or no exp claim');
      return;
    }

    console.log(`⏰ [AUTH] Scheduling token refresh in ${Math.round(delay / 1000)}s`);

    refreshTimerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;

      try {
        await getRefreshedTokenOnce();
      } catch (error) {
        console.error('[AUTH] Scheduled refresh failed');
        clearAuth();
      }
    }, delay);
  }, [getRefreshedTokenOnce, clearAuth]);

  /**
   * Initialize auth state on mount (hydrate from cookie or localStorage)
   */
  useEffect(() => {
    const initAuth = async () => {
      console.log('🚀 [AUTH] Initializing auth state...');

      // Check if user explicitly logged out - if so, skip auto-restore
      const explicitLogout = localStorage.getItem('explicitLogout');
      if (explicitLogout === 'true') {
        console.log('🚪 [AUTH] User explicitly logged out - skipping auto-restore');
        setAuthLoading(false);
        return;
      }

      // First, try to restore session from cookie (preferred)
      try {
        console.log('[AUTH] Attempting to restore session from cookie...');
        const response = await api.get('/refresh', {
          withCredentials: true
        });

        if (response.data?.accessToken) {
          const { accessToken, user } = response.data;
          if (user) {
            setAuth({ accessToken, user });
            console.log('✅ [AUTH] Session restored from cookie');
            setAuthLoading(false);
            return;
          }
        }
      } catch (error) {
        console.log('[AUTH] Regular cookie refresh failed, trying guest refresh:', error.response?.status);

        // Try guest session refresh
        try {
          const deviceId = getOrCreateDeviceId();
          console.log('[AUTH] Attempting guest session refresh with device_id:', deviceId);

          const guestResponse = await guestApi.get('/tour-guide/guest/refresh', {
            params: { device_id: deviceId }
          });

          if (guestResponse.data?.ok && guestResponse.data?.accessToken) {
            const { accessToken, tourId, guestParticipantId, name } = guestResponse.data;

            // Set auth context with guest credentials
            const guestUser = {
              id: `guest:${guestParticipantId}`,
              name: name || 'Guest',
              role: 'guest',
              guestParticipantId
            };

            setAuth({ accessToken, user: guestUser });
            console.log('✅ [AUTH] Guest session restored from cookie for tour:', tourId);
            setAuthLoading(false);
            return;
          }
        } catch (guestError) {
          console.log('[AUTH] Guest cookie refresh also failed, trying localStorage:', guestError.response?.status);
        }
      }

      // Fallback to localStorage (legacy)
      const token = TokenService.getAccessToken();
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        console.log('[AUTH] No stored credentials found');
        setAuthLoading(false);
        return;
      }

      try {
        const user = JSON.parse(savedUser);

        // Check if token is expired
        if (isTokenExpired(token)) {
          console.log('[AUTH] Stored token is expired');

          // Try to refresh for non-guests
          if (user.role !== 'guest') {
            try {
              TokenService.setAccessToken(token); // Temporarily set for refresh request
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

              await refreshAccessToken();
              console.log('[AUTH] Successfully refreshed expired token');
            } catch (error) {
              console.log('[AUTH] Refresh failed, clearing auth');
              clearAuth();
            }
          } else {
            // Guest token expired - clear
            console.log('[AUTH] Guest token expired, clearing');
            clearAuth();
          }
        } else {
          // Token is valid - restore auth
          console.log('[AUTH] Restoring valid token from localStorage');
          setAuth({ accessToken: token, user });
        }
      } catch (error) {
        console.error('[AUTH] Error during initialization:', error);
        clearAuth();
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();

    // Cleanup on unmount
    return () => {
      mountedRef.current = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []); // Only run on mount

  /**
   * Login function
   */
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/login`, {
        email,
        password
      }, {
        withCredentials: true // Allow refresh cookie
      });

      const { token, accessToken, user } = response.data;
      const finalToken = accessToken || token;

      if (!finalToken || !user) {
        throw new Error('Invalid login response');
      }

      // Set auth state
      setAuth({ accessToken: finalToken, user });

      // Handle return URL with strict policy
      const rawReturnUrl = localStorage.getItem('returnUrl');
      localStorage.removeItem('returnUrl');

      // Allow specific URLs as return destinations
      const computedRedirect = getSafeReturnUrl(rawReturnUrl);

      console.log('✅ [AUTH] Login successful');
      return { success: true, returnUrl: computedRedirect };
    } catch (err) {
      console.error('[AUTH] Login error:', err);
      const errorMsg = err.response?.data?.message || 'Login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  /**
   * Register function
   */
  const register = async (email, password, name) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/register`, {
        email,
        password,
        name
      });

      if (response.status === 201 || response.status === 200) {
        // Auto login after registration
        return await login(email, password);
      }

      return { success: true };
    } catch (err) {
      console.error('[AUTH] Registration error:', err);
      const errorMsg = err.response?.data?.message || 'Registration failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  /**
   * LINE Login function
   */
  const lineLogin = async (code, redirectUri) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/line-login`, {
        code,
        redirectUri
      }, {
        withCredentials: true
      });

      const { token, accessToken, user } = response.data;
      const finalToken = accessToken || token;

      if (!finalToken || !user) {
        throw new Error('Invalid LINE login response');
      }

      setAuth({ accessToken: finalToken, user });

      const rawReturnUrl = localStorage.getItem('returnUrl');
      localStorage.removeItem('returnUrl');

      // Allow specific URLs as return destinations
      const computedRedirect = getSafeReturnUrl(rawReturnUrl);

      console.log('✅ [AUTH] LINE login successful');
      return { success: true, returnUrl: computedRedirect };
    } catch (err) {
      console.error('[AUTH] LINE login error:', err);
      const errorMsg = err.response?.data?.message || 'LINE login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  /**
   * Apple Login function
   */
  const appleLogin = async (appleData) => {
    setError(null);
    try {
      const response = await axios.post(`${API_URL}/apple-login`, appleData, {
        withCredentials: true
      });

      const { token, accessToken, user } = response.data;
      const finalToken = accessToken || token;

      if (!finalToken || !user) {
        throw new Error('Invalid Apple login response');
      }

      setAuth({ accessToken: finalToken, user });

      const rawReturnUrl = localStorage.getItem('returnUrl');
      localStorage.removeItem('returnUrl');

      // Allow specific URLs as return destinations
      const computedRedirect = getSafeReturnUrl(rawReturnUrl);

      console.log('✅ [AUTH] Apple login successful');
      return { success: true, returnUrl: computedRedirect };
    } catch (err) {
      console.error('[AUTH] Apple login error:', err);
      const errorMsg = err.response?.data?.message || 'Apple login failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    try {
      await api.post('/logout', {}, { withCredentials: true });
      console.log('[AUTH] Logout API call successful');
    } catch (err) {
      console.error('[AUTH] Logout API error:', err);
    } finally {
      clearAuth();
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUserData = async () => {
    try {
      const response = await api.get('/user');
      const userData = response.data;

      localStorage.setItem('user', JSON.stringify(userData));
      setCurrentUser(userData);

      console.log('[AUTH] User data refreshed successfully');
      return true;
    } catch (err) {
      console.error('[AUTH] Error refreshing user data:', err);

      if (err.response?.status === 401) {
        clearAuth();
      }

      return false;
    }
  };

  /**
   * Generate LINE Login URL helper
   */
  const generateLineLoginUrl = (channelId, redirectUri, state = 'line_login') => {
    const lineAuthUrl = new URL('https://access.line.me/oauth2/v2.1/authorize');
    lineAuthUrl.searchParams.append('response_type', 'code');
    lineAuthUrl.searchParams.append('client_id', channelId);
    lineAuthUrl.searchParams.append('redirect_uri', redirectUri);
    lineAuthUrl.searchParams.append('state', state);
    lineAuthUrl.searchParams.append('scope', 'profile openid email');

    return lineAuthUrl.toString();
  };

  /**
   * Helper function to manage event participation in localStorage
   */
  const manageEventParticipation = (eventId, status, isAdd = true) => {
    if (!currentUser) return false;

    try {
      const key = status === 'interested' ? 'interestedEvents' : 'joiningEvents';
      const events = JSON.parse(localStorage.getItem(key)) || [];
      const idNumber = Number(eventId);

      if (isAdd) {
        if (!events.includes(idNumber) && !events.includes(String(eventId))) {
          events.push(idNumber);
          localStorage.setItem(key, JSON.stringify(events));
        }
      } else {
        const filtered = events.filter(id =>
          id !== idNumber && id !== String(eventId)
        );
        localStorage.setItem(key, JSON.stringify(filtered));
      }

      return true;
    } catch (err) {
      console.error('[AUTH] Error managing event participation:', err);
      return false;
    }
  };

  /**
   * Helper to get user's participation status for an event
   */
  const getUserEventStatus = (eventId) => {
    if (!currentUser) return { isInterested: false, isJoining: false };

    try {
      const interestedEvents = JSON.parse(localStorage.getItem('interestedEvents')) || [];
      const joiningEvents = JSON.parse(localStorage.getItem('joiningEvents')) || [];

      const idNumber = Number(eventId);
      const idString = String(eventId);

      const isInterested = interestedEvents.some(id =>
        id === idNumber || id === idString);

      const isJoining = joiningEvents.some(id =>
        id === idNumber || id === idString);

      return { isInterested, isJoining };
    } catch (err) {
      console.error('[AUTH] Error getting user event status:', err);
      return { isInterested: false, isJoining: false };
    }
  };

  /**
   * Get current access token (for axios interceptor)
   */
  const getAccessToken = () => {
    return TokenService.getAccessToken();
  };

  // Show loading screen while initializing
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '20px', color: '#666' }}>กำลังโหลด...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Context value
  const value = {
    // State
    currentUser,
    user: currentUser, // Alias for compatibility
    loading: authLoading,
    authLoading,
    error,
    isAuthenticated: !!currentUser,

    // Core methods
    setAuth,
    clearAuth,
    login,
    register,
    lineLogin,
    appleLogin,
    logout,
    refreshAccessToken: getRefreshedTokenOnce,
    refreshUserData,

    // Helpers
    generateLineLoginUrl,
    manageEventParticipation,
    getUserEventStatus,
    getAccessToken,

    // JWT utils
    decodeJWT,
    isTokenExpired
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
